import { useState } from 'react';
import type { Professional } from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Row,
  Select,
  SkeletonList,
  Stack,
} from '@dental-staffing-platform/ui';
import { adminApi } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';
import { humanize, professionalTone } from '../lib/format.js';

export function Professionals() {
  const [page, setPage] = useState(1);
  const [status, setStatusFilter] = useState('all');
  const [profession, setProfession] = useState('all');
  const [search, setSearch] = useState('');
  const { data, loading, error, reload } = useAsync(
    () =>
      adminApi.professionals.page({
        page,
        pageSize: 20,
        ...(status !== 'all' ? { status: status as Professional['status'] } : {}),
        ...(profession !== 'all' ? { profession: profession as Professional['profession'] } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      }),
    [page, status, profession, search]
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const set = async (id: string, status: Professional['status']) => {
    setBusy(id);
    setActionError(null);
    try {
      await adminApi.professionals.setStatus(id, status);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not update professional');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Professionals"
        subtitle="Review and approve professionals in your organization"
      />

      <Card style={{ marginBottom: 16 }}>
        <Row gap={12} style={{ alignItems: 'end', flexWrap: 'wrap' }}>
          <Field label="Search">
            <Input
              type="search"
              value={search}
              placeholder="Name or phone"
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </Field>
          <Field label="Status">
            <Select
              value={status}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </Select>
          </Field>
          <Field label="Profession">
            <Select
              value={profession}
              onChange={(event) => {
                setProfession(event.target.value);
                setPage(1);
              }}
            >
              <option value="all">All professions</option>
              <option value="hygienist">Hygienist</option>
              <option value="assistant">Assistant</option>
              <option value="front_desk">Front desk</option>
              <option value="dentist">Dentist</option>
            </Select>
          </Field>
        </Row>
      </Card>

      {actionError ? (
        <Card style={{ marginBottom: 12 }}>
          <span style={{ color: 'var(--dsp-danger-fg)' }}>{actionError}</span>
        </Card>
      ) : null}

      {loading ? (
        <SkeletonList count={4} />
      ) : error ? (
        <Card>
          <span style={{ color: 'var(--dsp-danger-fg)' }}>
            Couldn’t load professionals: {error}
          </span>
        </Card>
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title="No professionals yet"
          description="Professionals appear here as they join."
        />
      ) : (
        <Stack gap={12}>
          {data.items.map((p) => (
            <Card key={p.id}>
              <Row style={{ justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>
                    {p.firstName} {p.lastName}
                  </div>
                  <div className="dsp-muted" style={{ fontSize: '0.88rem', marginTop: 2 }}>
                    {humanize(p.profession)}
                    {p.yearsExperience != null ? ` · ${p.yearsExperience} yrs` : ''}
                  </div>
                </div>
                <Row gap={8} style={{ flex: 'none' }}>
                  <Badge tone={professionalTone(p.status)}>{humanize(p.status)}</Badge>
                  {p.status === 'active' ? (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => void set(p.id, 'suspended')}
                      disabled={busy === p.id}
                    >
                      Suspend
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => void set(p.id, 'active')}
                      disabled={busy === p.id}
                    >
                      {p.status === 'suspended' ? 'Reactivate' : 'Approve'}
                    </Button>
                  )}
                </Row>
              </Row>
            </Card>
          ))}
          <Pagination
            {...data}
            itemLabel="professionals"
            disabled={loading}
            onPageChange={setPage}
          />
        </Stack>
      )}
    </div>
  );
}
